import { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  LayoutDashboard,
  Route,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TOUR_VERSION = 'v2';

const steps = [
  {
    title: 'Welcome to GreenQuote',
    description: 'This dashboard is your daily workspace for quotes, customers, revenue, and upcoming service work.',
    icon: LayoutDashboard,
    accent: 'bg-emerald-700',
  },
  {
    title: 'Start with a quote',
    description: 'Use New Quote or Create Quote to price a property, save the estimate, and move the customer through your pipeline.',
    icon: FileText,
    accent: 'bg-emerald-700',
  },
  {
    title: 'Track open decisions',
    description: 'Pending shows quotes that still need follow-up, so new leads do not disappear after the first conversation.',
    icon: CheckCircle2,
    accent: 'bg-amber-600',
  },
  {
    title: 'Build your client list',
    description: 'Clients collects won quotes and active accounts, giving you a clean view of who you service and what they are worth monthly.',
    icon: Users,
    accent: 'bg-slate-800',
  },
  {
    title: 'Plan service work',
    description: 'Schedule gives you the calendar view. The dashboard also keeps this week visible so the next service window is always close.',
    icon: CalendarDays,
    accent: 'bg-sky-700',
  },
  {
    title: 'Optimize daily routes',
    description: 'Routes maps your client addresses, lets you select the day’s stops, and creates an efficient driving order from your start location.',
    icon: Route,
    accent: 'bg-teal-700',
  },
  {
    title: 'Set pricing once',
    description: 'Settings is where you configure pricing, add-ons, team access, billing, and integrations without cluttering the dashboard.',
    icon: SlidersHorizontal,
    accent: 'bg-violet-700',
  },
];

function getStorageKey(userId) {
  return `greenquote:onboarding-tour:${TOUR_VERSION}:${userId || 'anonymous'}`;
}

function hasCompletedTour(userId) {
  if (typeof window === 'undefined') return true;

  try {
    return window.localStorage.getItem(getStorageKey(userId)) === 'completed';
  } catch {
    return false;
  }
}

function completeTour(userId) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(getStorageKey(userId), 'completed');
  } catch {
    // If localStorage is blocked, finishing the current in-memory tour is enough.
  }
}

export default function OnboardingTour({ userId, startWhen = true }) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const forceTour = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('tour') === '1';
  }, []);

  useEffect(() => {
    if (!startWhen || !userId) return;

    if (forceTour || !hasCompletedTour(userId)) {
      setOpen(true);
      setCurrentStep(0);
    }
  }, [forceTour, startWhen, userId]);

  if (!open) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      completeTour(userId);
      setOpen(false);
      return;
    }

    setCurrentStep((stepIndex) => stepIndex + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((stepIndex) => Math.max(0, stepIndex - 1));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-700 text-xs font-bold text-white">
                GQ
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-950">GreenQuote quick tour</p>
                <p className="text-xs text-slate-500">
                  Step {currentStep + 1} of {steps.length}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {steps.map((item, index) => (
                <span
                  key={item.title}
                  className={`h-2 w-2 rounded-full ${index <= currentStep ? 'bg-emerald-700' : 'bg-slate-300'}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-7">
          <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-lg text-white ${step.accent}`}>
            <StepIcon className="h-7 w-7" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-950">{step.title}</h2>
          <p className="mt-3 text-base leading-7 text-slate-600">{step.description}</p>

          <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-950">First setup path</p>
            <p className="mt-1 text-sm leading-6 text-emerald-800">
              Create a quote, win the work, review the client, schedule service, then plan the route.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            type="button"
            onClick={handleNext}
            className="bg-emerald-700 hover:bg-emerald-800"
          >
            {isLastStep ? 'Finish Tour' : 'Next'}
            {!isLastStep && <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
